import React, {useEffect, useState} from 'react';


import {Link, useNavigate} from "react-router-dom";

import {
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Radio,
    Row,
    Select,
    Space,
    Typography,
} from 'antd';
import {CalendarIcon, PercentIcon, SettingsIcon, TagIcon, UsersIcon} from 'lucide-react';
import {URLS} from "@/lib/urls";

import {DatePicker} from 'antd-jalali';


import {useApiDeleteAdminDiscount, useApiUpdateAdminDiscount} from "@/api/discount";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {GenerateRandomDiscountCode} from "@/lib/utils/random";
import {DeleteOutlined} from "@ant-design/icons";
import CopyMe from "@/lib/helper/copyMe";
import dayjs from "dayjs";
import PermList from "@/lib/helper/PermList";
import {Can} from "@/components/can/Can";


const {Title, Text} = Typography;
const {TextArea} = Input;


function DiscountMasterDetailSection({discountData, id, isSuccess}) {


    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [generateBy, setGenerateBy] = useState('operator');
    const [code, setCode] = useState('')
    const [discountMode, setDiscountMode] = useState('percent');
    const [dataState, setDateState] = useState({
        publish_at: null,
        expired_at: null,
        total_capacity: null,
        capacity_per_user: false,
        category: null,
        user_description: null,
        notif_text: null,
    })

    const {mutate: updateDiscount, isPending: isPendingUpdate} = useApiUpdateAdminDiscount()
    const {mutate: deleteDiscount, isPending: isPendingDelete, isSuccess: isSuccessDelete} = useApiDeleteAdminDiscount()


    // Populate form when data is loaded
    useEffect(() => {
        if (discountData?.data?.data && isSuccess) {
            const data = discountData.data?.data;

            // Set form values
            form.setFieldsValue({
                title: data.title,
                generate_code_by: data.generate_code_by || 'operator',
                code: data.code,

                discount_mode: data.discount_mode || 'percent',
                percent: data.percent,
                max_amount: data.max_amount,
                allow_installment: data.allow_installment,
                amount: data.amount,
                allow_gender: data.allow_gender,
                total_capacity: data.total_capacity,
                capacity_per_user: data.capacity_per_user,
                status: data.status,
                notif_text: data?.notif_text,
            });

            // Set state values
            setGenerateBy(data.generate_code_by ?? 'operator');
            setDiscountMode(data.discount_mode ?? 'percent');
            setCode(data?.code);

            // Set date state
            setDateState({
                ...dataState,
                publish_at: data.publish_at,
                expired_at: data.expired_at,
                total_capacity: data.total_capacity,
                capacity_per_user: data.capacity_per_user,
                category: data.category,
                notif_text: data.notif_text,
                user_description: data.user_description,
            });
        }
    }, [discountData, isSuccess, form]);


    function handleGenerateRandomCode(generateIfEmpty = false) {
        if (generateIfEmpty && code) {
            // needless to generate new because have a code!
        } else {
            const code = GenerateRandomDiscountCode()
            form.setFieldsValue({code: code});
            setCode(code);
        }
    }


    const onFinish = (values) => {
        values.id = id; // Add id for API update
        values.code = code;
        values.category = dataState.category;
        values.generate_code_by = generateBy;
        values.publish_at = dataState.publish_at;
        values.expired_at = dataState.expired_at;
        values.total_capacity = dataState.total_capacity;
        values.capacity_per_user = dataState.capacity_per_user;
        values.user_description = dataState.user_description;
        values.notif_text = dataState.notif_text;

        updateDiscount(values, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r)
            },
            onError: (r) => {
                ApiHandleErrorMessage(r)
            }
        })
    };


    function onRemove() {
        deleteDiscount({id: id}, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r)
                navigate(URLS.admin.discount.dashboard)
            },
            onError: (r) => {
                ApiHandleErrorMessage(r)
            }
        })

    }

    return (
        <div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
            >
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        <Space direction="vertical" size="medium" className="w-full">
                            <Card
                                className="shadow-sm"
                                title={
                                    <div className="flex items-center gap-2">
                                        <TagIcon size={20} className="text-gray-600"/>
                                        <span>اطلاعات اصلی</span>
                                    </div>
                                }
                            >
                                <Form.Item name="title" label="عنوان">
                                    <Input size="medium" placeholder="عنوان توضیحی برای کد تخفیف"/>
                                </Form.Item>
                                <div className={'my-2'}>
                                    <span>نحوه تولید کد</span>
                                    <Radio.Group
                                        onChange={(e) => setGenerateBy(e.target.value)}
                                        className="w-full"
                                        optionType="button"
                                        value={generateBy}
                                        buttonStyle="solid"
                                    >
                                        <Radio value="operator">ورود دستی کد</Radio>
                                        <Radio value="random" onClick={() => handleGenerateRandomCode(true)}>تولید
                                            خودکار کد</Radio>
                                    </Radio.Group>
                                </div>

                                <Form.Item
                                    name="code"
                                    label="کد تخفیف"

                                >
                                    <Input
                                        size="medium"
                                        placeholder="مثال: NEWREGISTER"
                                        onChange={(e) => setCode(e.target.value)}
                                        value={code}
                                        disabled={generateBy === 'random'}
                                        addonBefore={<CopyMe data={code ?? ''}>کپی</CopyMe>}
                                        suffix={
                                            generateBy === 'random' && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() => handleGenerateRandomCode()}
                                                >
                                                    تولید کد جدید
                                                </Button>
                                            )
                                        }
                                    />

                                    <small className={'text-gray-500'}>این کد باید در اختیار مخاطب قرار گیرد تا در مراحل
                                        پرداخت از آن استفاده کنند</small>
                                </Form.Item>

                                <div className={'my-2'}>
                                    <span>دسته بندی</span>
                                    <Input size="medium" placeholder="مثال: کلاس‌های تابستان"
                                           onChange={(e) => setDateState((prev) => ({
                                               ...prev,
                                               category: e.target.value,
                                           }))}
                                           value={dataState.category}
                                    />
                                    <small className={'text-gray-500'}>برای یافتن سریع تر در میان کد های تخفیف در بخش
                                        مدیریت استفاده می‌شود</small>
                                </div>


                                <div>

                                    <Input.TextArea
                                        onChange={(e) => setDateState((prev) => ({
                                            ...prev,
                                            user_description: e.target.value,
                                        }))}
                                        value={dataState.user_description}
                                        rows={4}
                                        placeholder="اینجا بنویسید ..."
                                    />
                                    <small className={'text-gray-500'}>یادداشتی در خصوص این کد تخفیف را اینجا بنویسید تا
                                        در آینده از آن مطلع باشید</small>
                                </div>

                                <div className={'my-2'}>
                                    <span>متن پیامک اطلاع رسانی</span>
                                    <Input.TextArea
                                        onChange={(e) => setDateState((prev) => ({
                                            ...prev,
                                            notif_text: e.target.value,
                                        }))}
                                        value={dataState.notif_text}
                                        rows={4}
                                        placeholder="متن پیامک اطلاع رسانی"
                                    />
                                    <small className={'text-gray-500'}>متنی که می‌خواهید برای کاربران مجاز برای استفاده
                                        از این کد تخفیف پیامک شود را اینجا وارد کنید</small>
                                </div>
                            </Card>
                            <Card
                                className="shadow-sm mt-2"
                                title={
                                    <div className="flex items-center gap-2">
                                        <PercentIcon size={20} className="text-gray-600"/>
                                        <span>تنظیمات تخفیف</span>
                                    </div>
                                }
                            >
                                {/*<Form.Item name="type" label="نوع کد تخفیف">*/}
                                {/*    <Select size="medium">*/}
                                {/*        <Select.Option value="manually">دستی</Select.Option>*/}
                                {/*        <Select.Option value="dynamic">داینامیک</Select.Option>*/}
                                {/*    </Select>*/}
                                {/*</Form.Item>*/}

                                <Form.Item
                                    name="discount_mode"
                                    label="نوع تخفیف"
                                >
                                    <Radio.Group
                                        onChange={(e) => setDiscountMode(e.target.value)}
                                        className="w-full"
                                        optionType="button"
                                        buttonStyle="solid"
                                    >
                                        <Radio value="percent">درصدی</Radio>
                                        <Radio value="amount">مبلغ ثابت</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Row gutter={16}>
                                    {discountMode === 'percent' ? (
                                        <>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="percent"
                                                    label="درصد تخفیف"
                                                >
                                                    <InputNumber
                                                        size="medium"
                                                        min={0}
                                                        max={100}
                                                        placeholder="10"
                                                        suffix="%"
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="max_amount"
                                                    label="تا سقف حداکثر مبلغ تخفیف (تومان)"
                                                >
                                                    <InputNumber
                                                        size="medium"
                                                        min={0}
                                                        placeholder="500000"
                                                        formatter={(value) =>
                                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                        }
                                                        className="w-full"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </>
                                    ) : (
                                        <Col span={24}>
                                            <Form.Item
                                                name="amount"
                                                label="مبلغ تخفیف (تومان)"
                                            >
                                                <InputNumber
                                                    size="medium"
                                                    min={0}
                                                    placeholder="100000"
                                                    formatter={(value) =>
                                                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                    }
                                                    className="w-full"
                                                />
                                            </Form.Item>
                                        </Col>
                                    )}
                                </Row>
                            </Card>

                            <Card
                                className="shadow-sm mt-2"
                                title={
                                    <div className="flex items-center gap-2">
                                        <UsersIcon size={20} className="text-gray-600"/>
                                        <span>محدودیت‌ها و ظرفیت</span>
                                    </div>
                                }
                            >
                                <Form.Item name="allow_gender" label=" جنسیت مجاز برای استفاده از این کد تخفیف">
                                    <Select size="medium">
                                        <Select.Option value="both">همه</Select.Option>
                                        <Select.Option value="male">آقایان</Select.Option>
                                        <Select.Option value="female">بانوان</Select.Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item name="allow_installment"
                                           label="امکان استفاده از کد تخفیف در پرداخت نقدی و اقساط">
                                    <Select size="medium">
                                        <Select.Option value="both">پرداخت نقدی + پرداخت اقساط</Select.Option>
                                        <Select.Option value="cash">فقط پرداخت نقدی</Select.Option>
                                        <Select.Option value="installment">فقط پرداخت اقساط</Select.Option>
                                    </Select>
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="total_capacity" label="ظرفیت کل (نفر) ">

                                            <InputNumber
                                                value={dataState.total_capacity}
                                                onChange={(d) => setDateState((prev) => ({
                                                    ...prev,
                                                    total_capacity: d
                                                }))}
                                                size="medium"
                                                min={0}
                                                placeholder="100"
                                                className="w-full"
                                            />
                                            <small className={'text-gray-500'}>تعداد کل دفعات مجاز استفاده از این کد
                                                تخفیف</small>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="capacity_per_user"
                                            label="تعداد دفعات مجاز برای استفاده هر کاربر"
                                        >
                                            <InputNumber
                                                value={dataState.capacity_per_user}
                                                onChange={(d) => setDateState((prev) => ({
                                                    ...prev,
                                                    capacity_per_user: d
                                                }))}
                                                size="medium"
                                                min={1}
                                                placeholder="1"
                                                className="w-full"
                                            />
                                            <small className={'text-gray-500'}>هر کاربر چند بار از این کد می‌تواند
                                                استفاده کند</small>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                {/*<Form.Item name="usage_status" label="وضعیت استفاده">*/}
                                {/*    <Select size="medium">*/}
                                {/*        <Select.Option value="free">آزاد</Select.Option>*/}
                                {/*        <Select.Option value="full">پر</Select.Option>*/}
                                {/*    </Select>*/}
                                {/*</Form.Item>*/}
                            </Card>

                            <Card
                                className="shadow-sm mt-2"
                                title={
                                    <div className="flex items-center gap-2">
                                        <CalendarIcon size={20} className="text-gray-600"/>
                                        <span>زمان‌بندی</span>
                                    </div>
                                }
                            >
                                <Row gutter={16}>
                                    <Col span={8}>

                                        <div>
                                            <span>تاریخ و ساعت انتشار</span>
                                            <div>

                                                <DatePicker
                                                    showTime
                                                    format="YYYY/MM/DD HH:mm:ss"


                                                    value={
                                                        dataState.publish_at
                                                            ? dayjs(dataState.publish_at).calendar("jalali")
                                                            : null
                                                    }
                                                    onChange={(d) => setDateState({
                                                        ...dataState,
                                                        publish_at: d ? d.calendar("gregory") : null,
                                                    })}
                                                />
                                            </div>
                                            <small className={'text-gray-500'}>تا قبل از شروع زمان انتشار کد تخفیف فعال
                                                نخواهد بود</small>

                                        </div>

                                    </Col>
                                    <Col span={8}>
                                        <div>
                                            <span>تاریخ و ساعت انقضا</span>
                                            <div>

                                                <DatePicker
                                                    showTime
                                                    format="YYYY/MM/DD HH:mm:ss"

                                                    value={
                                                        dataState.expired_at
                                                            ? dayjs(dataState.expired_at).calendar("jalali")
                                                            : null
                                                    }
                                                    onChange={(d) => setDateState({
                                                        ...dataState,
                                                        expired_at: d ? d.calendar("gregory") : null,
                                                    })}
                                                />
                                            </div>
                                            <small className={'text-gray-500'}>بعد از گذشت از زمان انقضا کد تخفیف
                                                غیرفعال خواهد شد</small>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        </Space>

                    </Col>


                    <Col xs={24} lg={8}>
                        <div className="sticky">
                            <Card
                                className="shadow-sm sm:mt-2 md:mt-0"
                                title={
                                    <div className="flex items-center gap-2">
                                        <SettingsIcon size={20} className="text-gray-600"/>
                                        <span>تنظیمات انتشار</span>
                                    </div>
                                }
                            >
                                <Form.Item name="status" label="وضعیت">
                                    <Select size="medium">
                                        <Select.Option value="draft">پیش‌نویس</Select.Option>
                                        <Select.Option value="publish">منتشر شده</Select.Option>
                                        <Select.Option value="expire">منقضی شده</Select.Option>
                                        {/*<Select.Option value="full">پر شده</Select.Option>*/}
                                        <Select.Option value="disable">غیرفعال</Select.Option>
                                    </Select>
                                </Form.Item>
                                <Divider/>
                                <Space direction="vertical" className="w-full" size="middle">
                                    <Can permission={PermList.DISCOUNT_EDIT}>
                                        <Button
                                            type="primary"
                                            size="medium"
                                            htmlType="submit"
                                            block
                                            loading={isPendingUpdate}
                                            className="!bg-gray-800 hover:!bg-gray-900"
                                        >
                                            ذخیره کد تخفیف
                                        </Button>
                                    </Can>

                                    <Link to={URLS.admin.discount.list}>
                                        <Button size="medium" block>
                                            انصراف
                                        </Button>
                                    </Link>
                                </Space>
                            </Card>
                            <Can permission={PermList.DISCOUNT_DELETE}>
                                <Card className="shadow-sm mt-4 "
                                      style={{border: "1px solid #ff4d4f"}}

                                >
                                    <div className="text-center space-y-3">
                                        <Text type="secondary" className="text-sm block">
                                            برای حذف کد تخفیف کلیک کنید
                                        </Text>

                                        <Popconfirm
                                            title="حذف کد تخفیف"
                                            description="آیا از حذف کد تخفیف مطمئن هستید؟"
                                            onConfirm={onRemove}
                                            okText="بله، حذف شود"
                                            cancelText="انصراف"
                                            placement="top"
                                        >
                                            <Button
                                                danger
                                                icon={<DeleteOutlined/>}
                                                size="middle"
                                            >
                                                حذف کد تخفیف
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                </Card>
                            </Can>

                        </div>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}

export default DiscountMasterDetailSection;